/**
 * 日別天気 (telopCode 118 種、府県 / 週間予報) → 表示絵文字 / ラベル。
 * SoT: docs/specs/demand/jma-weather-data.md 付録「telopCode 対応表（118 種）」
 * (機械転写)。telopCode 118 種は固有絵文字を個別に持たせず、概況 4 分類
 * (晴/曇/雨/雪) 経由で spec §5 の絵文字に集約する (grill Q3/Q6)。
 *
 * `weatherCategoryEmojiOf` / `weatherCategoryLabelOf` は類似日検索ダイアログの
 * 候補行 (日次代表天気、backend `WeatherCategory` enum 1..4=晴/曇/雨/雪) 向けの
 * 別 helper 対 (telopCode 前提の telopEmojiOf / telopLabelOf とは配線元が異なる、
 * 設計書 demand-forecast-similar-day-display AC-01/AC-02)。CATEGORY_EMOJI を
 * 共有し、SUNNY のみ telopEmojiOf 同様に別定義する。
 */
import { WeatherCategory } from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'

type TelopCategory = '晴' | '曇' | '雨' | '雪'

type TelopEntry = {
  label: string
  category: TelopCategory
}

/**
 * telopCode 118 種の対応表 (内部専用、`src/models/weather/` の外へは export しない)。
 * SoT: docs/specs/demand/jma-weather-data.md 付録「telopCode 対応表（118 種）」を機械転写。
 * 概況は降水優先で分類済み (spec 付録の記載どおり)。
 */
const TELOP_TABLE: Record<number, TelopEntry> = {
  100: { label: '晴', category: '晴' },
  101: { label: '晴時々曇', category: '晴' },
  102: { label: '晴一時雨', category: '雨' },
  103: { label: '晴時々雨', category: '雨' },
  104: { label: '晴一時雪', category: '雪' },
  105: { label: '晴時々雪', category: '雪' },
  106: { label: '晴一時雨か雪', category: '雨' },
  107: { label: '晴時々雨か雪', category: '雨' },
  108: { label: '晴一時雨か雷雨', category: '雨' },
  110: { label: '晴後時々曇', category: '晴' },
  111: { label: '晴後曇', category: '晴' },
  112: { label: '晴後一時雨', category: '雨' },
  113: { label: '晴後時々雨', category: '雨' },
  114: { label: '晴後雨', category: '雨' },
  115: { label: '晴後一時雪', category: '雪' },
  116: { label: '晴後時々雪', category: '雪' },
  117: { label: '晴後雪', category: '雪' },
  118: { label: '晴後雨か雪', category: '雨' },
  119: { label: '晴後雨か雷雨', category: '雨' },
  120: { label: '晴朝夕一時雨', category: '雨' },
  121: { label: '晴朝の内一時雨', category: '雨' },
  122: { label: '晴夕方一時雨', category: '雨' },
  123: { label: '晴山沿い雷雨', category: '晴' },
  124: { label: '晴山沿い雪', category: '晴' },
  125: { label: '晴午後は雷雨', category: '雨' },
  126: { label: '晴昼頃から雨', category: '雨' },
  127: { label: '晴夕方から雨', category: '雨' },
  128: { label: '晴夜は雨', category: '雨' },
  130: { label: '朝の内霧後晴', category: '晴' },
  131: { label: '晴明け方霧', category: '晴' },
  132: { label: '晴朝夕曇', category: '晴' },
  140: { label: '晴時々雨で雷を伴う', category: '雨' },
  160: { label: '晴一時雪か雨', category: '雪' },
  170: { label: '晴時々雪か雨', category: '雪' },
  181: { label: '晴後雪か雨', category: '雪' },
  200: { label: '曇', category: '曇' },
  201: { label: '曇時々晴', category: '曇' },
  202: { label: '曇一時雨', category: '雨' },
  203: { label: '曇時々雨', category: '雨' },
  204: { label: '曇一時雪', category: '雪' },
  205: { label: '曇時々雪', category: '雪' },
  206: { label: '曇一時雨か雪', category: '雨' },
  207: { label: '曇時々雨か雪', category: '雨' },
  208: { label: '曇一時雨か雷雨', category: '雨' },
  209: { label: '霧', category: '曇' },
  210: { label: '曇後時々晴', category: '曇' },
  211: { label: '曇後晴', category: '曇' },
  212: { label: '曇後一時雨', category: '雨' },
  213: { label: '曇後時々雨', category: '雨' },
  214: { label: '曇後雨', category: '雨' },
  215: { label: '曇後一時雪', category: '雪' },
  216: { label: '曇後時々雪', category: '雪' },
  217: { label: '曇後雪', category: '雪' },
  218: { label: '曇後雨か雪', category: '雨' },
  219: { label: '曇後雨か雷雨', category: '雨' },
  220: { label: '曇朝夕一時雨', category: '雨' },
  221: { label: '曇朝の内一時雨', category: '雨' },
  222: { label: '曇夕方一時雨', category: '雨' },
  223: { label: '曇日中時々晴', category: '曇' },
  224: { label: '曇昼頃から雨', category: '雨' },
  225: { label: '曇夕方から雨', category: '雨' },
  226: { label: '曇夜は雨', category: '雨' },
  228: { label: '曇昼頃から雪', category: '雪' },
  229: { label: '曇夕方から雪', category: '雪' },
  230: { label: '曇夜は雪', category: '雪' },
  231: { label: '曇海上海岸は霧か霧雨', category: '曇' },
  240: { label: '曇時々雨で雷を伴う', category: '雨' },
  250: { label: '曇時々雪で雷を伴う', category: '雪' },
  260: { label: '曇一時雪か雨', category: '雪' },
  270: { label: '曇時々雪か雨', category: '雪' },
  281: { label: '曇後雪か雨', category: '雪' },
  300: { label: '雨', category: '雨' },
  301: { label: '雨時々晴', category: '雨' },
  302: { label: '雨時々止む', category: '雨' },
  303: { label: '雨時々雪', category: '雪' },
  304: { label: '雨か雪', category: '雨' },
  306: { label: '大雨', category: '雨' },
  308: { label: '雨で暴風を伴う', category: '雨' },
  309: { label: '雨一時雪', category: '雪' },
  311: { label: '雨後晴', category: '雨' },
  313: { label: '雨後曇', category: '雨' },
  314: { label: '雨後時々雪', category: '雪' },
  315: { label: '雨後雪', category: '雪' },
  316: { label: '雨か雪後晴', category: '雨' },
  317: { label: '雨か雪後曇', category: '雨' },
  320: { label: '朝の内雨後晴', category: '雨' },
  321: { label: '朝の内雨後曇', category: '雨' },
  322: { label: '雨朝晩一時雪', category: '雪' },
  323: { label: '雨昼頃から晴', category: '雨' },
  324: { label: '雨夕方から晴', category: '雨' },
  325: { label: '雨夜は晴', category: '雨' },
  326: { label: '雨夕方から雪', category: '雪' },
  327: { label: '雨夜は雪', category: '雪' },
  328: { label: '雨一時強く降る', category: '雨' },
  329: { label: '雨一時みぞれ', category: '雨' },
  340: { label: '雪か雨', category: '雪' },
  350: { label: '雨で雷を伴う', category: '雨' },
  361: { label: '雪か雨後晴', category: '雪' },
  371: { label: '雪か雨後曇', category: '雪' },
  400: { label: '雪', category: '雪' },
  401: { label: '雪時々晴', category: '雪' },
  402: { label: '雪時々止む', category: '雪' },
  403: { label: '雪時々雨', category: '雪' },
  405: { label: '大雪', category: '雪' },
  406: { label: '風雪強い', category: '雪' },
  407: { label: '暴風雪', category: '雪' },
  409: { label: '雪一時雨', category: '雪' },
  411: { label: '雪後晴', category: '雪' },
  413: { label: '雪後曇', category: '雪' },
  414: { label: '雪後雨', category: '雪' },
  420: { label: '朝の内雪後晴', category: '雪' },
  421: { label: '朝の内雪後曇', category: '雪' },
  422: { label: '雪昼頃から雨', category: '雪' },
  423: { label: '雪夕方から雨', category: '雪' },
  425: { label: '雪一時強く降る', category: '雪' },
  426: { label: '雪後みぞれ', category: '雪' },
  427: { label: '雪一時みぞれ', category: '雪' },
  450: { label: '雪で雷を伴う', category: '雪' },
}

const CATEGORY_EMOJI: Record<Exclude<TelopCategory, '晴'>, string> = {
  曇: '☁️',
  雨: '☔',
  雪: '❄️',
}

/**
 * telopCode → 表示絵文字。概況 4 分類経由で集約する (spec §5 決定)。
 * 晴概況は telopCode 100 のみ ☀️、他の晴概況 8 種は 🌤️ (grill Q3)。夜は晴概況一律 🌙。
 * 未知 code は '' (= 空アイコン降格、既知値へ倒さない)。
 */
export function telopEmojiOf(code: number, daytime: boolean): string {
  const entry = TELOP_TABLE[code]
  if (entry === undefined) return ''
  if (entry.category === '晴') {
    if (!daytime) return '🌙'
    return code === 100 ? '☀️' : '🌤️'
  }
  return CATEGORY_EMOJI[entry.category]
}

/** telopCode → 日本語ラベル。未知 code は '—'。 */
export function telopLabelOf(code: number): string {
  return TELOP_TABLE[code]?.label ?? '—'
}

/**
 * 類似日検索ダイアログ候補行の日次代表天気 (概況 4 分類、AC-01) → 表示絵文字。
 * UNSPECIFIED (= その過去日の天気実績なし) は '' (空アイコン降格、telopEmojiOf の
 * 未知 code 規約と同型)。
 *
 * TODO(#3805): 日次代表天気は昼夜切替なし (spec jma-weather-data §5 への注記待ち。
 * telopEmojiOf は時間帯別天気のため daytime を持つが、本 helper は日次代表のため
 * 常に昼絵文字を返す)。
 */
export function weatherCategoryEmojiOf(category: WeatherCategory): string {
  switch (category) {
    case WeatherCategory.SUNNY:
      return '☀️'
    case WeatherCategory.CLOUDY:
      return CATEGORY_EMOJI['曇']
    case WeatherCategory.RAINY:
      return CATEGORY_EMOJI['雨']
    case WeatherCategory.SNOWY:
      return CATEGORY_EMOJI['雪']
    default:
      return ''
  }
}

/**
 * 類似日検索ダイアログ候補行の日次代表天気 (概況 4 分類、AC-02) → 表示ラベル
 * (tooltip)。UNSPECIFIED は '—' (spec AC-017 の「—」慣例に準拠)。
 */
export function weatherCategoryLabelOf(category: WeatherCategory): string {
  switch (category) {
    case WeatherCategory.SUNNY:
      return '晴'
    case WeatherCategory.CLOUDY:
      return '曇'
    case WeatherCategory.RAINY:
      return '雨'
    case WeatherCategory.SNOWY:
      return '雪'
    default:
      return '—'
  }
}
